import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import secureApi from '../services/secureApiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);

  const authTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Manejo de errores de autenticación
  const handleAuthError = useCallback((msg = 'Sesión expirada') => {
    setUser(null);
    setIsAuthenticated(false);
    setError(msg);
    localStorage.removeItem('userToken'); // Synchronize with ProtectedRoute
    
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (authTimeoutRef.current) clearTimeout(authTimeoutRef.current);
  }, []);

  // Manejo de expiración de sesión
  const handleSessionExpiry = useCallback(() => {
    setSessionExpiry(new Date());
    handleAuthError();
  }, [handleAuthError]);

  // Monitoreo de sesión activa
  const startSessionMonitoring = useCallback(() => {
    // Heartbeat cada 5 minutos
    heartbeatRef.current = setInterval(async () => {
      try {
        await secureApi.secureRequest('/auth/heartbeat', { method: 'POST' });
      } catch (error) {
        console.warn('Heartbeat falló:', error.message);
        if (error.message.includes('No autorizado')) {
          handleAuthError();
        }
      }
    }, 5 * 60 * 1000);

    // Auto-logout por inactividad (30 minutos)
    const resetInactivityTimer = () => {
      if (authTimeoutRef.current) clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = setTimeout(() => {
        handleSessionExpiry();
      }, 30 * 60 * 1000);
    };

    // Escuchar actividad del usuario
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetInactivityTimer, true));

    resetInactivityTimer();

    return () => {
      events.forEach(event => document.removeEventListener(event, resetInactivityTimer, true));
    };
  }, [handleAuthError, handleSessionExpiry]);

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (token) {
          // En modo mock, asumimos que si hay token hay sesión
          const userData = secureApi.secureStorage.getSecureItem('user_data');
          if (userData) {
            setUser(userData);
            setIsAuthenticated(true);
            startSessionMonitoring();
          }
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        handleAuthError();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [startSessionMonitoring, handleAuthError]);

  // Login seguro
  const login = useCallback(async (credentials) => {
    setLoading(true);
    setError(null);

    try {
      if (!credentials.username || credentials.username.length < 3) {
        throw new Error('Usuario debe tener al menos 3 caracteres');
      }
      if (!credentials.password || credentials.password.length < 6) {
        throw new Error('Contraseña debe tener al menos 6 caracteres');
      }

      const result = await new Promise((resolve, reject) => {
        setTimeout(() => {
          if (credentials.username === 'demo' && credentials.password === 'nybanq2026') {
            resolve({ user: { id: 1, name: 'Demo Business', role: 'business' }, token: 'mock-token' });
          } else if (credentials.username === 'crossborder' && credentials.password === 'nybanq2026') {
            resolve({ user: { id: 3, name: 'Demo CrossBorder', role: 'cross_border' }, token: 'mock-token' });
          } else if (credentials.username.length >= 3 && credentials.password.length >= 6) {
            resolve({ user: { id: 2, name: credentials.username, role: 'business' }, token: 'mock-token' });
          } else {
            reject(new Error('Credenciales inválidas. Hint: demo / nybanq2026'));
          }
        }, 1500);
      });

      if (result.user) {
        setUser(result.user);
        setIsAuthenticated(true);
        localStorage.setItem('userToken', result.token); // Store token for ProtectedRoute
        startSessionMonitoring();
        return { success: true, user: result.user };
      }
      throw new Error('Respuesta de login inválida');
    } catch (error) {
      const errorMessage = error.message || 'Error en el login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [startSessionMonitoring]);

  // Logout seguro
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await secureApi.secureLogout();
    } catch (error) {
      console.warn('Error en logout:', error.message);
    } finally {
      handleAuthError('Sesión cerrada');
      setLoading(false);
    }
  }, [handleAuthError]);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    sessionExpiry,
    login,
    logout,
    clearError: () => setError(null)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
